import matter from 'gray-matter';
import { BlogPost } from '../types';

// Interface for blog front matter structure (used implicitly by gray-matter parsing)

interface BlogDiscoveryCache {
  posts: BlogPost[];
  lastUpdated: number;
}

// Cache duration: 5 minutes in development, 1 hour in production
const CACHE_DURATION = process.env.NODE_ENV === 'development' ? 5 * 60 * 1000 : 60 * 60 * 1000;

let cache: BlogDiscoveryCache | null = null;

/**
 * Generates a slug from filename by removing .md extension and replacing special characters
 */
function generateSlugFromFilename(filename: string): string {
  return filename
    .replace('.md', '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Extracts date from front matter or falls back to filename pattern
 */
function extractDate(frontMatter: any, filename: string): string {
  if (frontMatter.date) {
    // If date is a Date object, format it
    if (frontMatter.date instanceof Date) {
      return frontMatter.date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    return frontMatter.date;
  }

  // Try to extract date from filename (YYYY-MM-DD-title.md format)
  const dateMatch = filename.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateMatch) {
    const date = new Date(dateMatch[1]);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // Default to current date if no date found
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Extracts excerpt from front matter or content
 */
function extractExcerpt(frontMatter: any, content: string): string {
  if (frontMatter.excerpt) {
    return frontMatter.excerpt;
  }

  // Extract first paragraph from content as excerpt
  const firstParagraph = content
    .split('\n')
    .find(line => line.trim().length > 0 && !line.startsWith('#'));
  
  if (firstParagraph) {
    // Limit to 150 characters
    return firstParagraph.length > 150 
      ? firstParagraph.substring(0, 150) + '...'
      : firstParagraph;
  }

  return 'No excerpt available';
}

/**
 * Fetches and parses a single markdown file
 */
async function parseMarkdownFile(filename: string): Promise<BlogPost | null> {
  try {
    const response = await fetch(`/blog/${filename}`);
    
    if (!response.ok) {
      console.warn(`Failed to fetch ${filename}: ${response.status}`);
      return null;
    }

    const content = await response.text();
    const { data: frontMatter, content: markdownContent } = matter(content);

    // Generate slug from front matter or filename
    const slug = frontMatter.slug || generateSlugFromFilename(filename);

    // Extract title from front matter or first heading
    let title = frontMatter.title;
    if (!title) {
      const titleMatch = markdownContent.match(/^#\s+(.+)$/m);
      title = titleMatch ? titleMatch[1] : filename.replace('.md', '');
    }

    const blogPost: BlogPost = {
      id: Date.now() + Math.random(), // Generate unique ID
      title,
      date: extractDate(frontMatter, filename),
      excerpt: extractExcerpt(frontMatter, markdownContent),
      markdownFile: filename,
      slug
    };

    return blogPost;
  } catch (error) {
    console.error(`Error parsing ${filename}:`, error);
    return null;
  }
}

/**
 * Discovers all markdown files in the public/blog directory
 */
async function discoverMarkdownFiles(): Promise<string[]> {
  try {
    // In a production environment, you might want to maintain a manifest file
    // For now, we'll try to fetch common files and expand as needed
    
    // Try to fetch a manifest file first (optional)
    try {
      const manifestResponse = await fetch('/blog/manifest.json');
      if (manifestResponse.ok) {
        const manifest = await manifestResponse.json();
        return manifest.files || [];
      }
    } catch {
      // Manifest doesn't exist, continue with fallback
    }

    // Fallback: try known files and scan for common patterns
    const commonFiles = [
      'first-post.md',
      'test-post.md',
      'react-hooks.md',
      'css-grid.md', 
      'javascript-best-practices.md'
    ];

    const existingFiles: string[] = [];
    
    for (const filename of commonFiles) {
      try {
        const response = await fetch(`/blog/${filename}`, { method: 'HEAD' });
        if (response.ok) {
          existingFiles.push(filename);
        }
      } catch {
        // File doesn't exist, skip
      }
    }

    return existingFiles;
  } catch (error) {
    console.error('Error discovering markdown files:', error);
    return [];
  }
}

/**
 * Fetches all blog posts with caching
 */
export async function getBlogPosts(): Promise<BlogPost[]> {
  // Check cache first
  if (cache && (Date.now() - cache.lastUpdated) < CACHE_DURATION) {
    return cache.posts;
  }

  try {
    // Discover markdown files
    const filenames = await discoverMarkdownFiles();
    
    if (filenames.length === 0) {
      console.warn('No markdown files found in /blog directory');
      return [];
    }

    // Parse all markdown files
    const parsePromises = filenames.map(parseMarkdownFile);
    const parsedPosts = await Promise.all(parsePromises);

    // Filter out null results and sort by date (newest first)
    const validPosts = parsedPosts
      .filter((post): post is BlogPost => post !== null)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // Update cache
    cache = {
      posts: validPosts,
      lastUpdated: Date.now()
    };

    return validPosts;
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    
    // Return cached posts if available, otherwise empty array
    return cache?.posts || [];
  }
}

/**
 * Clears the blog posts cache
 */
export function clearBlogCache(): void {
  cache = null;
}

/**
 * Gets a single blog post by slug
 */
export async function getBlogPost(slug: string): Promise<BlogPost | null> {
  const posts = await getBlogPosts();
  return posts.find(post => post.slug === slug) || null;
}